import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useAliId } from '../hooks/useAliId';
import {
  createUpgradeSession,
  verifyUpgrade,
  getPortalUrl,
} from '../utils/subscriptionApi';
import { BillingPeriodToggle } from './BillingPeriodToggle';
import { SubscriptionPage } from './subscription';
import { featureFlags } from '../utils/featureFlags';
import type { BillingPeriod } from '../utils/paymentTiers';

interface UpgradePlan {
  id: 'creator' | 'commercial';
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  features: string[];
}

/** Map subscription tier IDs to display names */
const TIER_DISPLAY_NAMES: Record<string, string> = {
  commercial: 'Commercial Catalog',
  catalog_owner: 'Creator',
  creator: 'Creator',
};

const getTierDisplayName = (tier: string): string =>
  TIER_DISPLAY_NAMES[tier] || tier;

const formatUpgradePrice = (plan: UpgradePlan, billingPeriod: BillingPeriod): string => {
  if (billingPeriod === 'monthly') return `$${plan.monthlyPrice}/mo`;
  return `$${plan.yearlyPrice}/year`;
};

const formatUpgradeBillingDetail = (plan: UpgradePlan, billingPeriod: BillingPeriod): string | null => {
  if (billingPeriod === 'yearly') return `$${(plan.yearlyPrice / 12).toFixed(2)}/mo avg`;
  return null;
};

const ALL_UPGRADE_PLANS: UpgradePlan[] = [
  {
    id: 'creator',
    name: 'Creator',
    monthlyPrice: 15,
    yearlyPrice: 150,
    description: 'For individual artists and collectors',
    features: [
      'Single catalog',
      'Up to 3 delegates',
      'Up to 3 legacy managers',
      'Assigned ALI ID',
    ],
  },
  {
    id: 'commercial',
    name: 'Commercial Catalog',
    monthlyPrice: 60,
    yearlyPrice: 650,
    description: 'For galleries and institutions',
    features: [
      'Multiple catalogs',
      'Up to 650 catalog items',
      'Up to 10 delegates',
      'Up to 3 legacy managers',
      'Assigned ALI ID',
    ],
  },
];

export const SubscriptionTab: React.FC = () => {
  const { user } = useAuth();
  const { permissions, refreshPermissions } = usePermissions();
  const { aliId } = useAliId();

  const [selectedPlan, setSelectedPlan] = useState<'creator' | 'commercial' | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [initials, setInitials] = useState('');
  const [initialsEdited, setInitialsEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const isViewer = permissions?.role === 'viewer';
  const isPaying = permissions?.role === 'catalog-owner' ||
    (permissions?.role === 'admin' && permissions?.subscriptionTier != null);

  // Filter upgrade plans based on feature flags
  const upgradePlans = useMemo(() => {
    if (featureFlags.isEnabled('enableGeneralCatalog')) return ALL_UPGRADE_PLANS;
    return ALL_UPGRADE_PLANS.filter((p) => p.id !== 'commercial');
  }, []);

  const savingsPercentage = useMemo(() => {
    const percentages = upgradePlans
      .map((p) => Math.round(((p.monthlyPrice * 12 - p.yearlyPrice) / (p.monthlyPrice * 12)) * 100));
    return percentages.length > 0 ? Math.max(...percentages) : 0;
  }, [upgradePlans]);

  // Auto-suggest initials
  useEffect(() => {
    if (!initialsEdited && user?.firstName && user?.lastName) {
      setInitials(`${user.firstName[0].toUpperCase()}${user.lastName[0].toUpperCase()}`);
    }
  }, [user?.firstName, user?.lastName, initialsEdited]);

  // Handle Stripe return
  const handleStripeReturn = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const upgradeSuccess = urlParams.get('upgrade_success');
    const sessionId = urlParams.get('session_id');
    const upgradeCancelled = urlParams.get('upgrade_cancelled');

    if (upgradeCancelled === 'true') {
      setError('Upgrade was cancelled. You can try again when ready.');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (upgradeSuccess === 'true' && sessionId) {
      setIsLoading(true);
      setError(null);

      try {
        const result = await verifyUpgrade(sessionId);
        if (result.success) {
          setSuccessMessage(
            `Upgrade successful! Your new ALI ID is ${result.aliId || 'being generated'}. ` +
            'Please sign out and sign back in for changes to take full effect.'
          );
          await refreshPermissions();
        } else {
          setError('Upgrade verification failed. Please contact support.');
        }
      } catch (err) {
        const message = (err as Error).message || 'Failed to verify upgrade. Please contact support.';
        setError(message);
      } finally {
        setIsLoading(false);
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [refreshPermissions]);

  useEffect(() => {
    handleStripeReturn();
  }, [handleStripeReturn]);

  const handleUpgrade = async () => {
    if (!selectedPlan) {
      setError('Please select a plan.');
      return;
    }

    if (!initials || initials.length !== 2 || !/^[A-Za-z]{2}$/.test(initials)) {
      setError('Please enter exactly 2 letters for your initials.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await createUpgradeSession(selectedPlan, initials.toUpperCase(), billingPeriod);
      window.location.href = result.url;
    } catch (err) {
      const message = (err as Error).message || 'Failed to start upgrade. Please try again.';
      setError(message);
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError(null);

    try {
      const url = await getPortalUrl();
      window.location.href = url;
    } catch (err) {
      const message = (err as Error).message || 'Unable to open billing portal. Please contact support.';
      setError(message);
      setPortalLoading(false);
    }
  };

  // Paying creator users with the new in-app management page enabled get the
  // full Variation A surface. Commercial users (and creator users with the flag
  // off) fall through to the legacy chip-plus-portal-link view below.
  if (
    isPaying &&
    permissions?.subscriptionTier === 'creator' &&
    featureFlags.isEnabled('enableSubscriptionManagement')
  ) {
    return <SubscriptionPage />;
  }

  // Paying user view
  if (isPaying) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Subscription
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="body1">Current Plan:</Typography>
          <Chip
            label={getTierDisplayName(permissions?.subscriptionTier || 'creator')}
            color="primary"
          />
        </Box>

        {aliId && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            ALI ID: {aliId}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleManageBilling}
          disabled={portalLoading}
          startIcon={portalLoading ? <CircularProgress size={20} /> : undefined}
        >
          {portalLoading ? 'Opening...' : 'Manage Billing'}
        </Button>
      </Box>
    );
  }

  // Viewer upgrade view
  if (isViewer) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upgrade Your Plan
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body1">Current Plan:</Typography>
          <Chip label="Free Plan" variant="outlined" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upgrade to create your own catalog, manage delegates, and get your unique ALI ID.
        </Typography>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <BillingPeriodToggle
          value={billingPeriod}
          onChange={setBillingPeriod}
          savingsPercentage={savingsPercentage}
        />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {upgradePlans.map((plan) => (
            <Grid size={{ xs: 12, md: 6 }} key={plan.id}>
              <Card
                variant={selectedPlan === plan.id ? 'elevation' : 'outlined'}
                sx={{
                  height: '100%',
                  border: selectedPlan === plan.id ? '2px solid' : '1px solid',
                  borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: 2,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => setSelectedPlan(plan.id)}
                  sx={{ height: '100%' }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6">{plan.name}</Typography>
                      {selectedPlan === plan.id && <CheckCircleIcon color="primary" />}
                    </Box>

                    <Typography variant="h5" color="primary" sx={{ my: 1, fontWeight: 'bold' }}>
                      {formatUpgradePrice(plan, billingPeriod)}
                    </Typography>

                    {formatUpgradeBillingDetail(plan, billingPeriod) && (
                      <Typography variant="caption" color="text.secondary">
                        {formatUpgradeBillingDetail(plan, billingPeriod)}
                      </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>

                    {plan.features.map((feature, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 0.5,
                          '&::before': { content: '"•"', mr: 1, color: 'primary.main' },
                        }}
                      >
                        {feature}
                      </Typography>
                    ))}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {selectedPlan && (
          <TextField
            fullWidth
            label="Initials"
            value={initials}
            onChange={(e) => {
              setInitialsEdited(true);
              setInitials(e.target.value.toUpperCase().slice(0, 2));
            }}
            margin="normal"
            required
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            helperText="Your 2-letter initials will be used in your ALI ID (e.g., JD → JD-3K7)"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Your ALI ID uniquely identifies you and your catalog items.">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        )}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleUpgrade}
            disabled={isLoading || !selectedPlan}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            {isLoading ? 'Processing...' : 'Upgrade Now'}
          </Button>
        </Box>

        {selectedPlan && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You will be redirected to Stripe to complete payment.
          </Alert>
        )}
      </Box>
    );
  }

  // Fallback for other roles
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Contact support for subscription management.
      </Typography>
    </Box>
  );
};

export default SubscriptionTab;
